import { useState, useEffect, useCallback } from 'react';

import { useIntl } from 'react-intl';

import useCommonToast from '../../hooks/useCommonToast';

import SimpleGridSkeleton from '../utils/SimpleGridSkeleton';

import { getPortfolioItems } from 'services/portfolios';
import mappedItemTypes from 'services/items/mappedItemTypes';
import ReviewForwardData from './ReviewForwardData';

const DataReviewTab = ({
    formRef,
    otherProperties,
    runSchedule,
    curvePriceTypeId,
    curveSourceId,
    assignedIndex = -1,
    selectedIndex = 0,
    selectedPortfolios = [],
    ...rest
}) => {
    const { toast } = useCommonToast();
    const intl = useIntl();

    const [isLoading, setIsLoading] = useState(false);
    const [forwardCurves, setForwardCurves] = useState([]);
    const [selectedPortfolioIds, setSelectedPortfolioIds] = useState([]);

    // Fetch the items to review for the selected portfolios
    const fetchPortfolioItems = useCallback(
        async (portfolioIds) => {
            try {
                //variables to contain the different item types to review
                let curveItems = [];

                // loop through each portfolio and collect the items to review
                for (const portfolioId of portfolioIds) {
                    const portfolioItems = await getPortfolioItems(portfolioId, true);

                    //collect the forward curve items distinct by id
                    const forwardCurveTypeItems = portfolioItems
                        .filter((item) => item.typeId === mappedItemTypes.FORWARD_CURVE.id)
                        .flatMap((item) => item.items || []);
                    curveItems = [
                        ...new Map([...curveItems, ...forwardCurveTypeItems].map((item) => [item.id, item])).values(),
                    ];
                }

                //sort the forward curves by name and set the state
                curveItems.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setForwardCurves(curveItems);
            } catch (error) {
                toast({
                    title: intl.formatMessage({ id: 'common_error' }),
                    description: error.message,
                    status: 'error',
                });
            }
        },
        [toast, intl]
    );

    // Fetch the data when the component mounts or when the selected portfolios change
    useEffect(() => {
        const fetchData = async () => {
            if (!isLoading) {
                try {
                    const portfolioIds = selectedPortfolios?.map((p) => p.properties?.id);

                    if (
                        portfolioIds !== null &&
                        portfolioIds.length > 0 &&
                        (selectedPortfolioIds.length !== portfolioIds.length ||
                            !portfolioIds.every((id, idx) => id === selectedPortfolioIds[idx]))
                    ) {
                        setIsLoading(true);
                        await fetchPortfolioItems(portfolioIds);
                        setSelectedPortfolioIds(portfolioIds);
                    }
                } catch (error) {
                    toast({
                        title: intl.formatMessage({ id: 'common_error' }),
                        description: error.message,
                        status: 'error',
                    });
                } finally {
                    setIsLoading(false);
                }
            }
        };

        if (selectedIndex === assignedIndex) {
            fetchData();
        }
    }, [
        fetchPortfolioItems,
        selectedPortfolios,
        selectedPortfolioIds,
        selectedIndex,
        assignedIndex,
        intl,
        toast,
        isLoading,
    ]);

    return (
        <>
            {isLoading ? (
                <SimpleGridSkeleton columns={{ xl: 3 }} spacingX={6} spacingY={4} mb={4} />
            ) : (
                <>
                    <ReviewForwardData
                        forwardCurves={forwardCurves}
                        formRef={formRef}
                        otherProperties={otherProperties}
                        runSchedule={runSchedule}
                        curvePriceTypeId={curvePriceTypeId}
                        curveSourceId={curveSourceId}
                        assignedIndex={assignedIndex}
                        selectedIndex={selectedIndex}
                        portfolios={selectedPortfolios}
                    />
                </>
            )}
        </>
    );
};

export default DataReviewTab;
